type ThingsboardTokenResponse = {
  token?: string | { token: string }
} | string

let tbToken = ''

function getBaseUrl() {
  return useRuntimeConfig().public.thingsboardBase as string
}

function getStoredToken() {
  if (typeof window !== 'undefined') {
    const stored = window.localStorage.getItem('tb-token')
    if (stored) {
      tbToken = stored
    }
  }
  return tbToken
}

function saveToken(token: string) {
  tbToken = token
  if (typeof window !== 'undefined') {
    window.localStorage.setItem('tb-token', token)
  }
}

function clearToken() {
  tbToken = ''
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem('tb-token')
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${getBaseUrl()}${path}`
  const token = getStoredToken()
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(init.headers as Record<string, string> | undefined),
  }
  const response = await fetch(url, {
    ...init,
    headers,
  })
  if (!response.ok) {
    const errorText = await response.text().catch(() => '')
    throw new Error(`ThingsBoard request failed (${response.status}): ${errorText}`)
  }
  return (await response.json()) as T
}

function parseToken(response: any): string {
  if (!response) {
    throw new Error('Brak odpowiedzi od ThingsBoard')
  }
  if (typeof response === 'string') {
    return response
  }
  if (typeof response.token === 'string') {
    return response.token
  }
  if (response.token?.token) {
    return response.token.token
  }
  throw new Error('Nieprawidłowy token ThingsBoard')
}

function normalizeDevice(device: any, attrs: Record<string, any>): Device {
  return {
    id: device?.id?.id || device.id || '',
    name: device.name || device.label || 'Bez nazwy',
    status: attrs.status ?? device.type ?? 'unknown',
    location: attrs.location ?? device.label ?? 'Brak lokalizacji',
    latitude: Number(attrs.latitude ?? 0),
    longitude: Number(attrs.longitude ?? 0),
    lastSeen: attrs.lastSeen ?? device.createdTime ?? '',
    active: attrs.active === true || attrs.active === 'true',
  }
}

async function getServerAttributes(deviceId: string, keys: string[] = []) {
  const query = keys.length ? `&keys=${keys.join(',')}` : ''
  const entries = await request<any[]>(`/api/plugins/telemetry/DEVICE/${deviceId}/attributes?scope=SERVER_SCOPE${query}`)
  return (entries as any[]).reduce<Record<string, any>>((acc, entry) => {
    acc[entry.key] = entry.value ?? entry['value']
    return acc
  }, {})
}

export function useThingsboard() {
  const login = async (username: string, password: string): Promise<string> => {
    const response = await request<ThingsboardTokenResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    })
    const token = parseToken(response)
    saveToken(token)
    return token
  }

  const logout = async (): Promise<void> => {
    clearToken()
  }

  const isAuthenticated = (): boolean => {
    return !!getStoredToken()
  }

  const getDevices = async (): Promise<Device[]> => {
    const response = await request<{ data: any[] }>('/api/customer/devices?pageSize=100&page=0')
    const devices = response.data ?? []
    const mapped = await Promise.all(
      (devices as any[]).map(async (device) => {
        const attrs = await getServerAttributes(device.id.id || device.id, [
          'location',
          'latitude',
          'longitude',
          'status',
          'lastSeen',
          'active',
        ])
        const normalized = normalizeDevice(device, attrs)
        normalized.active = attrs.active === true || attrs.active === 'true'
        return normalized
      }),
    )
    return mapped.filter((device) => device.active)
  }

  const registerDevice = async (device: Device): Promise<Device> => {
    const payload = {
      name: device.name,
      type: 'default',
    }
    const response = await request<any>('/api/device', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    const deviceId = response.id?.id ?? response.id
    if (!deviceId) {
      throw new Error('Nie udało się utworzyć urządzenia w ThingsBoard')
    }
    const attributesPayload = {
      location: device.location || '',
      latitude: device.latitude ?? 0,
      longitude: device.longitude ?? 0,
      active: true,
    }
    await request<any>(`/api/plugins/telemetry/DEVICE/${deviceId}/attributes?scope=SERVER_SCOPE`, {
      method: 'POST',
      body: JSON.stringify(attributesPayload),
    })
    return {
      id: deviceId,
      name: device.name,
      status: 'new',
      location: device.location || '',
      latitude: device.latitude ?? 0,
      longitude: device.longitude ?? 0,
      active: true,
      lastSeen: String(Date.now()),
    }
  }

  const updateDeviceLocation = async (
    deviceId: string,
    location: string,
    latitude: number,
    longitude: number,
  ) => {
    await request<any>(`/api/plugins/telemetry/DEVICE/${deviceId}/attributes?scope=SERVER_SCOPE`, {
      method: 'POST',
      body: JSON.stringify({
        location,
        latitude,
        longitude,
      }),
    })
  }

  return {
    login,
    logout,
    isAuthenticated,
    getDevices,
    registerDevice,
    updateDeviceLocation,
  }
}
